import { useEffect, useState, useRef, useCallback } from "react";
import { SEOHead } from "@/components/SEOHead";
import { getBookingPageSchema } from '@/content/properties';
import { Waves, Map, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/layout/Header";

import blueRiverCampImage from "@/assets/properties/blue-river-camp.jpg";
import propertyMap from "@/assets/maps/blue-river-camp-map.webp";

const BookBlueRiverCamp = () => {
  const [zoom, setZoom] = useState(1);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((prev) => Math.min(Math.max(0.5, prev + delta), 4));
  }, []);

  const resetZoom = () => {
    setZoom(1);
    if (mapContainerRef.current) {
      mapContainerRef.current.scrollTo(0, 0);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsMapOpen(open);
    if (!open) {
      setZoom(1);
      setIsDragging(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      if (mapContainerRef.current) {
        setScrollStart({
          x: mapContainerRef.current.scrollLeft,
          y: mapContainerRef.current.scrollTop,
        });
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !mapContainerRef.current) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    mapContainerRef.current.scrollLeft = scrollStart.x - dx;
    mapContainerRef.current.scrollTop = scrollStart.y - dy;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "//bluerivercamp.checkfront.com/lib/interface--0.js";
    script.type = "text/javascript";
    script.async = true;

    script.onload = () => {
      if ((window as any).DROPLET) {
        new (window as any).DROPLET.Widget({
          host: "bluerivercamp.checkfront.com",
          target: "CHECKFRONT_WIDGET_01",
          item_id: "76,225,300,78,3,303",
          category_id: "2",
          options: "tabs",
          style: "color: fff",
          provider: "droplet",
        }).render();
      }
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <>
      <SEOHead
        title="Book Son's Blue River Camp - Day Visit | Son's Getaways"
        description="Book your day visit at Son's Blue River Camp. Reserve your spot for tubing, swimming, and outdoor fun on the beautiful San Marcos River."
        canonical="/book-now/blue-river-camp"
        schemaJson={getBookingPageSchema("blue-river-camp")}
      />

      <div className="min-h-screen relative">
        <div className="fixed inset-0 -z-10">
          <img src={blueRiverCampImage} alt="Son's Blue River Camp" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50 md:backdrop-blur-0 backdrop-blur-md" />
        </div>

        <Header />

        <div className="relative z-10 min-h-screen pt-32 md:pt-40 pb-12 px-0 md:px-4">
          <div className="container max-w-5xl mx-auto">
            <div className="text-center mb-4 md:mb-8">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xs md:text-sm uppercase tracking-widest font-medium mb-2 md:mb-4 flex items-center justify-center gap-2"
                style={{ color: "#12b6da" }}
              >
                <Waves className="w-3 h-3 md:w-4 md:h-4" />
                Blue River Camp
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="font-display text-2xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-wide mb-2 md:mb-4"
              >
                Book Your <span className="text-accent">Day Visit</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-white/80 text-sm md:text-lg mb-2 md:mb-6 hidden md:block"
              >
                Select your date and rental option below
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <Dialog open={isMapOpen} onOpenChange={handleOpenChange}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white backdrop-blur-sm"
                    >
                      <Map className="w-4 h-4 mr-2" />
                      View Property Map
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl [&>button]:text-white [&>button]:opacity-100 [&>button>svg]:h-5 [&>button>svg]:w-5">
                    <div className="absolute top-12 right-4 flex flex-col gap-2 z-10">
                      <Button
                        size="icon"
                        variant="secondary"
                        onClick={() => setZoom((prev) => Math.min(prev + 0.25, 4))}
                        className="h-8 w-8"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="secondary"
                        onClick={() => setZoom((prev) => Math.max(prev - 0.25, 0.5))}
                        className="h-8 w-8"
                      >
                        <ZoomOut className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="secondary" onClick={resetZoom} className="h-8 w-8">
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center mb-2">
                      Scroll to zoom{zoom > 1 ? " • Drag to pan" : ""} • {Math.round(zoom * 100)}%
                    </p>
                    <div
                      ref={mapContainerRef}
                      className={`overflow-auto max-h-[80vh] select-none ${zoom > 1 ? (isDragging ? "cursor-grabbing" : "cursor-grab") : "cursor-default"}`}
                      onWheel={handleWheel}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseLeave}
                    >
                      <img
                        src={propertyMap}
                        alt="Blue River Camp Property Map"
                        className="rounded-lg"
                        style={{
                          width: `${zoom * 100}%`,
                          minWidth: `${zoom * 100}%`,
                          height: "auto",
                        }}
                        draggable={false}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="md:bg-white/10 md:backdrop-blur-md md:border md:border-white/20 md:rounded-2xl p-0 md:p-6 lg:p-8 md:shadow-xl [&_*]:text-white [&_a]:text-accent [&_a:hover]:text-accent/80 [&_input]:text-primary [&_select]:text-primary [&_button]:text-primary"
            >
              <div id="CHECKFRONT_WIDGET_01" className="min-h-[300px]">
                <p
                  id="CHECKFRONT_LOADER"
                  className="text-white/70 flex items-center justify-center gap-3 py-8 md:py-12"
                >
                  <span className="inline-block w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin"></span>
                  Searching Availability...
                </p>
              </div>
              <noscript>
                <a
                  href="https://bluerivercamp.checkfront.com/reserve/"
                  className="text-accent hover:underline text-lg"
                >
                  Continue to Secure Booking System »
                </a>
              </noscript>
            </motion.div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 h-16 z-10 pointer-events-none">
          <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-full">
            <path d="M0,60 L0,30 Q300,0 600,30 T1200,30 L1200,60 Z" fill="hsl(var(--background))" fillOpacity="0.3" />
          </svg>
        </div>
      </div>
    </>
  );
};

export default BookBlueRiverCamp;
